import { useSalesLocale } from '../../locale';

import React from 'react';
import { Button, Card, Space, Tag, Typography } from 'antd';
import type { SalesNotification } from '../../services/phase5/notificationCenter';
import { notificationCategoryLabels, notificationDisplayCopy, notificationPriorityLabels } from '../../services/phase5/notificationPresentation';
import './notifications.css';

const { Text } = Typography;

interface NotificationItemProps {
  item: SalesNotification;
  canDismiss: boolean;
  onOpen: (item: SalesNotification) => void;
  onMarkRead: (item: SalesNotification) => void;
  onDismiss: (item: SalesNotification) => void;
}

const priorityColor: Record<SalesNotification['priority'], string> = {
  critical: 'red',
  high: 'volcano',
  normal: 'blue',
  low: 'default',
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  item,
  canDismiss,
  onOpen,
  onMarkRead,
  onDismiss,
}) => {
  const { t: salesText, format: salesFormat, language } = useSalesLocale();
  const copy = notificationDisplayCopy(item, salesText, salesFormat);
  const hasDestination = Boolean(item.related_lead_id || item.action_url || ['open_task', 'open_workmail'].includes(String(item.action_payload?.action)));
  const created = new Date(item.created_at);

  return (
    <Card size="small" className="sales-notification-item" style={{ borderColor: item.is_read ? '#e3ddec' : '#b9addc' }}>
      <Space orientation="vertical" size={6} style={{ width: '100%' }}>
        <div className="sales-notification-item__heading">
          <Text strong>{copy.title}</Text>
          <Space wrap size={4}>
            <Tag color={priorityColor[item.priority]}>{salesText(notificationPriorityLabels[item.priority])}</Tag>
            <Tag>{salesText(notificationCategoryLabels[item.category] ?? item.category)}</Tag>
          </Space>
        </div>
        <Text type="secondary">{copy.description}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {Number.isNaN(created.getTime()) ? salesText('Not set') : created.toLocaleString(language === 'ar' ? 'ar-EG' : 'en-GB')}
        </Text>
        <Space wrap>
          <Button size="small" type="primary" onClick={() => onOpen(item)}>{salesText(hasDestination ? 'Open' : 'Acknowledge')}</Button>
          {!item.is_read ? (
            <Button size="small" onClick={() => onMarkRead(item)}>{salesText("Mark as Read")}</Button>
          ) : null}
          <Button size="small" disabled={!canDismiss} onClick={() => onDismiss(item)}>{salesText("Dismiss")}</Button>
        </Space>
        {!hasDestination && <Text type="secondary" style={{ fontSize: 12 }}>{salesText('No linked page for this notification.')}</Text>}
        {!canDismiss && <Text type="secondary" style={{ fontSize: 12 }}>{salesText('Critical notifications stay visible.')}</Text>}
      </Space>
    </Card>
  );
};
